import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface VariantPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  image: string;
  variants: string[];
  variantLabel?: string;
  currentVariant?: string;
  /** Text tlačidla, napr. „Pridať darček“ / „Pridať do košíka“ */
  confirmLabel?: string;
  /** Doplnkový riadok s cenou */
  priceNode?: React.ReactNode;
  onConfirm: (variant: string) => void;
}

export const VariantPickerDialog = ({
  open,
  onOpenChange,
  name,
  image,
  variants,
  variantLabel = "Variant",
  currentVariant,
  confirmLabel = "Potvrdiť výber",
  priceNode,
  onConfirm,
}: VariantPickerDialogProps) => {
  const [value, setValue] = useState<string | undefined>(currentVariant);

  useEffect(() => {
    if (open) setValue(currentVariant ?? (variants.length === 1 ? variants[0] : undefined));
  }, [open, currentVariant, variants]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm rounded-2xl">
        <DialogHeader className="text-left">
          <DialogTitle className="text-base">Vyberte {variantLabel.toLowerCase()}</DialogTitle>
          <DialogDescription className="sr-only">
            Voľba variantu pre {name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <img
            src={image}
            alt={name}
            className="w-14 h-14 rounded-xl object-cover bg-secondary flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight">{name}</p>
            {priceNode && <div className="mt-0.5">{priceNode}</div>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {variants.map((v) => {
            const active = value === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setValue(v)}
                className={cn(
                  "h-9 px-3.5 rounded-full border text-sm font-medium transition-all flex items-center gap-1.5",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:border-primary/50"
                )}
              >
                {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                {v}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!value}
          onClick={() => {
            if (!value) return;
            onConfirm(value);
            onOpenChange(false);
          }}
          className={cn(
            "mt-1 h-11 w-full rounded-xl font-bold text-sm transition-all",
            value
              ? "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.99]"
              : "bg-secondary text-muted-foreground cursor-not-allowed"
          )}
        >
          {value ? confirmLabel : `Zvoľte ${variantLabel.toLowerCase()}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
